import type { PostingDiscoveryReq } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import { type Filter, FilterId } from '../../../filters/types/filters';
import {
  buildActivityFilter,
  buildMultiActivityFilter,
  getActivityTaxonomyId,
} from './activity';
import { buildCompanyFilter, buildMultiCompanyFilter } from './company';
import { buildKeywordsFilter, buildTitlesFilter } from './free-text-filters';
import { buildGeographyFilter, buildMultiGeographyFilter } from './geography';
import { buildIndustryFilter, buildMultiIndustryFilter } from './industry';
import {
  buildMultiRemoteTypeFilter,
  buildMultiSeniorityFilter,
  buildRemoteTypeFilter,
  buildSeniorityFilter,
} from './list-filters';
import { composeFilterNodes } from './node-builders';
import {
  buildMultiSalaryFilter,
  buildPostDateFilter,
  buildSalaryFilter,
} from './range-filters';
import {
  buildMultiRoleFilter,
  buildRoleFilter,
  getRoleTaxonomyNumber,
} from './roles';
import {
  buildCustomSkillFilter,
  buildMultiCustomSkillFilter,
  getSkillTaxonomyId,
} from './skills';

const DEFAULT_PAGE_SIZE = 1000;

export const buildPostingDiscoveryFilters = (
  filters: Filter[],
  /** See `buildTalentDiscoveryFiltersV3`: the taxonomy geography ids resolve against. */
  geoTax: number | undefined
): PostingDiscoveryReq => {
  const hasMultiSeniority = findFilterById(filters, FilterId.MULTI_SENIORITY);
  const hasMultiCustomSkill = findFilterById(
    filters,
    FilterId.MULTI_CUSTOM_SKILL
  );
  const hasMultiPay = findFilterById(filters, FilterId.MULTI_PAY);
  const hasMultiActivity = findFilterById(filters, FilterId.MULTI_ACTIVITY);
  const hasMultiRemoteType = findFilterById(
    filters,
    FilterId.MULTI_REMOTE_TYPE
  );

  const seniorityFilter = hasMultiSeniority
    ? null
    : buildSeniorityFilter(filters);
  const customSkillFilter = hasMultiCustomSkill
    ? null
    : buildCustomSkillFilter(filters);
  const salaryFilter = hasMultiPay ? null : buildSalaryFilter(filters);
  const remoteTypeFilter = hasMultiRemoteType
    ? null
    : buildRemoteTypeFilter(filters);

  // Tree filters
  const companyFilter = buildCompanyFilter(filters);
  const industryFilter = buildIndustryFilter(filters);
  const geographyFilter = buildGeographyFilter(filters);
  const roleFilter = buildRoleFilter(filters);
  const activityFilter = hasMultiActivity ? null : buildActivityFilter(filters);

  // Free text filters
  const keywordsFilter = buildKeywordsFilter(filters);
  const titlesFilter = buildTitlesFilter(filters);

  // Date filter
  const postDateFilter = buildPostDateFilter(filters);

  // Multi filters
  const multiCompanyFilter = buildMultiCompanyFilter(filters);
  const multiIndustryFilter = buildMultiIndustryFilter(filters);
  const multiGeographyFilter = buildMultiGeographyFilter(filters);
  const multiRoleFilter = buildMultiRoleFilter(filters);
  const multiCustomSkillFilter = buildMultiCustomSkillFilter(filters);
  const multiActivityFilter = buildMultiActivityFilter(filters);
  const multiSeniorityFilter = buildMultiSeniorityFilter(filters);
  const multiSalaryFilter = buildMultiSalaryFilter(filters);
  const multiRemoteTypeFilter = buildMultiRemoteTypeFilter(filters);

  const allFilterNodes = [
    // Single filters
    seniorityFilter,
    remoteTypeFilter,
    customSkillFilter,
    salaryFilter,
    companyFilter,
    industryFilter,
    geographyFilter,
    roleFilter,
    activityFilter,
    keywordsFilter,
    titlesFilter,
    postDateFilter,
    // Multi filters
    multiCompanyFilter,
    multiIndustryFilter,
    multiGeographyFilter,
    multiRoleFilter,
    multiCustomSkillFilter,
    multiActivityFilter,
    multiSeniorityFilter,
    multiSalaryFilter,
    multiRemoteTypeFilter,
  ];

  const expr = composeFilterNodes(allFilterNodes);

  const activityTax = getActivityTaxonomyId(filters);

  return {
    filters: {
      expr,
      roles_tax: getRoleTaxonomyNumber(filters),
      skills_tax: getSkillTaxonomyId(filters),
      ...(activityTax === undefined ? {} : { activity_taxonomy: activityTax }),
      ...(geoTax === undefined ? {} : { geo_tax: geoTax }),
    },
    options: {
      include_descriptions: true,
    },
    pagination: {
      page_num: 1,
      page_size: DEFAULT_PAGE_SIZE,
    },
  };
};
